/**
 * Image utility functions for frontend
 * Handles blur image URL generation and management
 * Uses Cloudflare image resizing for optimization
 */

import { getBlurPlaceholder } from './imageOptimization';

/**
 * Check if a URL/path is already a tiny image
 * @param {string} url - The URL or path to check
 * @returns {boolean} - True if it's a tiny image
 */
export function isTinyImage(url) {
  return url?.includes('.tiny.jpg') || url?.includes('width=20') || false;
}

/**
 * Get the tiny image URL from an original image URL
 * Uses Cloudflare image resizing to generate a 20px wide blur placeholder
 * Falls back to .tiny.jpg convention if not a Cloudflare URL
 * @param {string} originalUrl - The original image URL
 * @returns {string} - The tiny image URL
 */
export function getTinyImageUrl(originalUrl) {
  if (!originalUrl) {
    return null;
  }

  // If already a tiny image, return as is
  if (isTinyImage(originalUrl)) {
    return originalUrl;
  }

  // Use Cloudflare image resizing for blur placeholder
  const optimized = getBlurPlaceholder(originalUrl);

  // If optimization returned the same URL (not a Cloudflare URL),
  // fall back to .tiny.jpg convention
  if (optimized === originalUrl) {
    const lastDotIndex = originalUrl.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return `${originalUrl}.tiny.jpg`;
    }
    const withoutExt = originalUrl.substring(0, lastDotIndex);
    return `${withoutExt}.tiny.jpg`;
  }

  return optimized;
}

/**
 * Get the original image URL from a tiny image URL
 * @param {string} tinyUrl - The tiny image URL
 * @returns {string} - The original image URL
 */
export function getOriginalImageUrl(tinyUrl) {
  if (!tinyUrl) {
    return null;
  }

  if (!isTinyImage(tinyUrl)) {
    return tinyUrl;
  }

  // Remove .tiny from the filename
  // https://domain.com/path/image.tiny.jpg -> https://domain.com/path/image.jpg
  return tinyUrl.replace('.tiny.jpg', '.jpg');
}

/**
 * Preload an image and return a promise
 * @param {string} url - The image URL to preload
 * @returns {Promise<Image>} - Promise that resolves when image is loaded
 */
export function preloadImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('No URL provided'));
      return;
    }

    const img = new Image();

    img.onload = () => {
      resolve(img);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };

    img.src = url;
  });
}

/**
 * Check if an image URL is accessible (can be loaded)
 * @param {string} url - The image URL to check
 * @returns {Promise<boolean>} - True if image can be loaded
 */
export async function canLoadImage(url) {
  try {
    await preloadImage(url);
    return true;
  } catch {
    return false;
  }
}

const imageUtils = {
  isTinyImage,
  getTinyImageUrl,
  getOriginalImageUrl,
  preloadImage,
  canLoadImage,
};

export default imageUtils;
